import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  House, Lightning, Drop, Thermometer, Hammer, HardHat, PaintBrush, Wall,
  Buildings, TreeEvergreen, Warning, CheckCircle,
  Clock, CurrencyDollar, CaretDown, ShieldCheck, Users, Briefcase,
  Certificate, Tag, Sparkle, Camera, Microphone, Translate, ClipboardText, BellRinging,
  CalendarCheck, ShoppingCart, GoogleLogo
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import { LOGO_URL } from '../config/branding';
import { trackConversion, trackVideoEvent } from '../utils/analytics';
import VoiceTaskSplitMock from '../components/landing/VoiceTaskSplitMock';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRADES = [
  { id: 'general', label: 'General Contractor' },
  { id: 'kitchen_remodel', label: 'Kitchen Remodel' },
  { id: 'bathroom_remodel', label: 'Bathroom Remodel' },
  { id: 'roofing', label: 'Roofing' },
  { id: 'painting', label: 'Painting' },
  { id: 'flooring', label: 'Flooring' },
  { id: 'electrical', label: 'Electrical' },
  { id: 'plumbing', label: 'Plumbing' },
  { id: 'hvac', label: 'HVAC' },
  { id: 'carpentry', label: 'Carpentry' },
  { id: 'concrete', label: 'Concrete/Masonry' },
  { id: 'landscaping', label: 'Landscaping' },
  { id: 'deck_patio', label: 'Deck/Patio' },
  { id: 'windows_doors', label: 'Windows & Doors' },
  { id: 'siding', label: 'Siding' },
  { id: 'emergency', label: 'Emergency Repair' },
];

const RADIUS_OPTIONS = [10, 25, 50, 100];
const CREW_SIZES = [
  { id: 1, label: 'Solo' },
  { id: 3, label: '2–3 crew' },
  { id: 6, label: '4–6 crew' },
  { id: 12, label: '7–12 crew' },
  { id: 25, label: '13+ crew' },
];

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'];

const inputCls = "w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none focus:border-[#EA580C] text-neutral-900 dark:text-white";
const labelCls = "block text-xs font-bold uppercase text-neutral-500 mb-1";
const SectionTitle = ({ n, children }) => (
  <h3 className="flex items-center gap-2 font-black text-neutral-900 dark:text-white text-lg mb-4">
    <span className="w-7 h-7 rounded-full bg-[#EA580C] text-white text-sm font-black flex items-center justify-center border-2 border-black">{n}</span>
    {children}
  </h3>
);

export default function ContractorFunnel() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [showServices, setShowServices] = useState(false);

  const [form, setForm] = useState({
    primary_trade: '',
    services: [],
    company_name: '',
    years_in_business: '',
    license_number: '',
    insured: false,
    city: '',
    state: '',
    zip_code: '',
    service_radius: 25,
    crew_size: 1,
    project_min_budget: '',
    project_max_budget: '',
    avg_rate: '',
    rate_unit: 'hour',
    name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const startedRef = React.useRef(false);
  const update = (field, value) => {
    if (!startedRef.current) {
      startedRef.current = true;
      trackConversion('contractor_funnel_start');
    }
    setForm(p => ({ ...p, [field]: value }));
  };
  const toggleService = (s) => setForm(p => ({
    ...p,
    services: p.services.includes(s) ? p.services.filter(x => x !== s) : [...p.services, s],
  }));

  const validate = () => (
    !!form.primary_trade &&
    form.company_name.trim().length >= 2 &&
    Number(form.years_in_business) >= 0 && form.years_in_business !== '' &&
    !!form.city && !!form.state &&
    Number(form.crew_size) >= 1 &&
    !!form.name.trim() &&
    !!form.email.trim() && form.email.includes('@') &&
    form.password.length >= 6 &&
    form.password === form.confirm_password
  );

  const handleSubmit = async () => {
    if (!validate()) {
      toast({ title: 'Almost there', description: 'Please fill in all required fields (marked *).', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        years_in_business: Number(form.years_in_business) || 0,
        service_radius: Number(form.service_radius) || 25,
        crew_size: Number(form.crew_size) || 1,
        project_min_budget: form.project_min_budget ? Number(form.project_min_budget) : null,
        project_max_budget: form.project_max_budget ? Number(form.project_max_budget) : null,
        avg_rate: form.avg_rate ? Number(form.avg_rate) : null,
      };
      delete payload.confirm_password;
      const pendingRef = localStorage.getItem('pending_referral_code');
      if (pendingRef) payload.referral_code = pendingRef;
      const res = await axios.post(`${API_URL}/api/contractor-funnel/submit`, payload);
      if (res.data?.success) {
        trackConversion('contractor_signup');
        localStorage.removeItem('pending_referral_code');
        if (res.data.token && res.data.user) {
          localStorage.setItem('token', res.data.token);
          localStorage.setItem('userType', res.data.user.user_type);
          localStorage.setItem('userId', res.data.user._id);
          localStorage.setItem('user', JSON.stringify(res.data.user));
          window.dispatchEvent(new Event('storage'));
        }
        setResult(res.data);
        setSubmitted(true);
        setTimeout(() => { window.location.href = '/contractor-dashboard?onboarding=1'; }, 2800);
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to submit', variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  if (submitted && result) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center p-4" data-testid="contractor-funnel-success">
        <div className="max-w-lg w-full text-center">
          <div className="w-20 h-20 bg-[#EA580C] rounded-full flex items-center justify-center mx-auto mb-6 border-4 border-black shadow-[4px_4px_0px_#000]">
            <CheckCircle size={48} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-black text-white mb-2">Welcome aboard!</h1>
          <p className="text-neutral-400 mb-4">Your contractor profile is live. Here's what's waiting for you.</p>
          <div className="grid grid-cols-3 gap-4 mb-8">
            <div className="bg-white/5 border border-white/10 p-4 rounded-lg">
              <p className="text-2xl font-black text-[#EA580C]">{result.matched_leads}</p>
              <p className="text-[10px] text-neutral-400 uppercase">Matching Leads</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-4 rounded-lg">
              <p className="text-2xl font-black text-emerald-400">{result.matched_jobs}</p>
              <p className="text-[10px] text-neutral-400 uppercase">Open Jobs</p>
            </div>
            <div className="bg-white/5 border border-white/10 p-4 rounded-lg">
              <p className="text-sm font-black text-[#2B4C6F] truncate">{result.primary_trade_label}</p>
              <p className="text-[10px] text-neutral-400 uppercase mt-1">Primary Trade</p>
            </div>
          </div>
          <p className="text-neutral-500 text-sm mb-6">Redirecting to your dashboard with a quick tour of the tools...</p>
          <div className="mb-3 bg-gradient-to-br from-[#EA580C]/20 to-transparent border-2 border-[#EA580C] rounded-lg p-4 text-left" data-testid="success-ai-scheduler-callout">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-[#EA580C] rounded-lg flex items-center justify-center border-2 border-black">
                <CalendarCheck size={20} weight="fill" className="text-white" />
              </div>
              <div className="flex-1">
                <p className="font-black text-white text-sm">AI Scheduling Engine</p>
                <p className="text-xs text-neutral-300 mt-1">Describe any job → AI builds the phase plan and books your best available crew. Find it in your <span className="font-bold text-[#EA580C]">header — AI Scheduler</span>.</p>
              </div>
            </div>
          </div>
          <div className="mb-6 bg-gradient-to-br from-emerald-500/20 to-transparent border-2 border-emerald-500 rounded-lg p-4 text-left" data-testid="success-translator-callout">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center border-2 border-black">
                <Microphone size={20} weight="fill" className="text-white" />
              </div>
              <div className="flex-1">
                <p className="font-black text-white text-sm">Voice Translator (12 Languages)</p>
                <p className="text-xs text-neutral-300 mt-1">Talk to any crew, any language. Find it in your <span className="font-bold text-emerald-400">Dashboard hero tiles</span>.</p>
              </div>
            </div>
          </div>
          <div className="flex gap-3 justify-center">
            <button onClick={() => { window.location.href = '/contractor-dashboard?onboarding=1'; }} className="h-12 px-6 bg-[#EA580C] hover:bg-[#C2410C] text-white font-bold rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all" data-testid="go-dashboard-btn">Go to Dashboard</button>
            <button onClick={() => navigate('/')} className="h-12 px-6 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg border border-white/20 transition-all" data-testid="return-home-btn">Return Home</button>
          </div>
        </div>
      </div>
    );
  }

  const passwordMismatch = form.confirm_password && form.password !== form.confirm_password;

  return (
    <div className="min-h-screen bg-[#F0F0F2] dark:bg-neutral-900" data-testid="contractor-funnel">
      {/* Header */}
      <header className="bg-neutral-900 border-b-4 border-[#EA580C]">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <img src={LOGO_URL} alt="ConstructHelp" className="h-10 w-auto" />
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-center mb-6">
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-neutral-900 dark:text-white">Join as a Contractor</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-2">One quick form — free to join, and we'll match you with leads instantly.</p>
        </div>

        {/* Single-page signup form */}
        <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-3xl p-6 sm:p-8 shadow-sm space-y-8">

          {/* 1. Trade */}
          <div data-testid="section-trade">
            <SectionTitle n={1}>Your trade</SectionTitle>
            <label className={labelCls}>Primary Trade *</label>
            <select
              value={form.primary_trade}
              onChange={(e) => update('primary_trade', e.target.value)}
              className={inputCls}
              data-testid="trade-select"
            >
              <option value="">Select your trade...</option>
              {TRADES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <button
              type="button"
              onClick={() => setShowServices(!showServices)}
              className="mt-3 text-xs font-bold text-[#EA580C] flex items-center gap-1"
              data-testid="toggle-services-btn"
            >
              <CaretDown size={12} weight="bold" className={`transition-transform ${showServices ? 'rotate-180' : ''}`} />
              {showServices ? 'Hide' : 'Add'} other services (optional){form.services.length > 0 ? ` — ${form.services.length} selected` : ''}
            </button>
            {showServices && (
              <div className="flex flex-wrap gap-2 mt-3">
                {TRADES.filter(t => t.id !== form.primary_trade).map(t => (
                  <button key={t.id} type="button" onClick={() => toggleService(t.id)}
                    className={`px-3 py-1.5 text-xs font-bold rounded-full border-2 transition-all ${form.services.includes(t.id) ? 'bg-[#EA580C] text-white border-[#EA580C]' : 'bg-white dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600 hover:border-[#EA580C]'}`}
                    data-testid={`service-${t.id}`}>
                    {form.services.includes(t.id) ? <CheckCircle size={12} weight="bold" className="inline mr-1" /> : null}{t.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* 2. Business */}
          <div data-testid="section-business">
            <SectionTitle n={2}>Your business</SectionTitle>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Company Name *</label>
                <input value={form.company_name} onChange={(e) => update('company_name', e.target.value)} maxLength={120}
                  className={inputCls} placeholder="e.g. Acme Construction LLC" data-testid="company-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Years in Business *</label>
                  <input type="number" min="0" value={form.years_in_business} onChange={(e) => update('years_in_business', e.target.value)}
                    className={inputCls} placeholder="e.g. 8" data-testid="years-input" />
                </div>
                <div>
                  <label className={labelCls}>License # (optional)</label>
                  <input value={form.license_number} onChange={(e) => update('license_number', e.target.value)}
                    className={inputCls} placeholder="e.g. LIC-12345" data-testid="license-input" />
                </div>
              </div>
              <button type="button" onClick={() => update('insured', !form.insured)}
                className={`w-full flex items-center gap-3 p-4 rounded-2xl border-2 bg-white dark:bg-neutral-900 shadow-sm transition-all ${form.insured ? 'border-[#EA580C] bg-[#EA580C]/5 shadow-md' : 'border-neutral-200 dark:border-neutral-600 hover:border-[#EA580C]/40'}`}
                data-testid="insured-toggle">
                <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${form.insured ? 'bg-[#EA580C] border-[#EA580C]' : 'border-neutral-400'}`}>
                  {form.insured && <CheckCircle size={16} weight="bold" className="text-white" />}
                </div>
                <div className="text-left flex-1">
                  <p className="font-bold text-sm text-neutral-900 dark:text-white flex items-center gap-2"><ShieldCheck size={16} weight="bold" className="text-emerald-500" /> I carry liability insurance</p>
                  <p className="text-[11px] text-neutral-500">Insured pros earn 2× more leads on average.</p>
                </div>
              </button>
            </div>
          </div>

          {/* 3. Service area */}
          <div data-testid="section-area">
            <SectionTitle n={3}>Where you work</SectionTitle>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className={labelCls}>Home City *</label>
                <input value={form.city} onChange={(e) => update('city', e.target.value)}
                  className={inputCls} placeholder="e.g. Austin" data-testid="city-input" />
              </div>
              <div>
                <label className={labelCls}>State *</label>
                <select value={form.state} onChange={(e) => update('state', e.target.value)} className={inputCls} data-testid="state-select">
                  <option value="">Select</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>ZIP Code</label>
                <input value={form.zip_code} onChange={(e) => update('zip_code', e.target.value)}
                  className={inputCls} placeholder="e.g. 78701" data-testid="zip-input" />
              </div>
              <div>
                <label className={labelCls}>Service Radius</label>
                <select value={form.service_radius} onChange={(e) => update('service_radius', Number(e.target.value))} className={inputCls} data-testid="radius-select">
                  {RADIUS_OPTIONS.map(r => <option key={r} value={r}>{r} miles</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* 4. Capacity & pricing */}
          <div data-testid="section-capacity">
            <SectionTitle n={4}>Capacity & pricing</SectionTitle>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className={labelCls}>Crew Size *</label>
                <select value={form.crew_size} onChange={(e) => update('crew_size', Number(e.target.value))} className={inputCls} data-testid="crew-select">
                  {CREW_SIZES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Average Rate (optional)</label>
                <div className="flex gap-2">
                  <div className="flex items-center flex-1 bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl overflow-hidden">
                    <span className="px-2.5 text-neutral-500"><CurrencyDollar size={16} weight="bold" /></span>
                    <input type="number" value={form.avg_rate} onChange={(e) => update('avg_rate', e.target.value)} placeholder="85"
                      className="flex-1 h-12 px-1 text-sm bg-transparent text-neutral-900 dark:text-white focus:outline-none min-w-0" data-testid="avg-rate" />
                  </div>
                  <select value={form.rate_unit} onChange={(e) => update('rate_unit', e.target.value)}
                    className="h-12 px-2 text-sm bg-white dark:bg-neutral-900 border border-neutral-300 dark:border-neutral-600 rounded-xl focus:outline-none text-neutral-900 dark:text-white" data-testid="rate-unit">
                    <option value="hour">/hr</option>
                    <option value="project">/project</option>
                  </select>
                </div>
              </div>
            </div>
            <label className={labelCls}>Typical Project Budget Range ($) (optional)</label>
            <div className="grid grid-cols-2 gap-3">
              <input type="number" value={form.project_min_budget} onChange={(e) => update('project_min_budget', e.target.value)} placeholder="Min (e.g. 2000)"
                className={inputCls} data-testid="project-min" />
              <input type="number" value={form.project_max_budget} onChange={(e) => update('project_max_budget', e.target.value)} placeholder="Max (e.g. 50000)"
                className={inputCls} data-testid="project-max" />
            </div>
          </div>

          {/* 5. Account */}
          <div data-testid="section-account">
            <SectionTitle n={5}>Create your account</SectionTitle>
            <button
              type="button"
              onClick={() => {
                trackConversion('contractor_google_click');
                // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                const redirectUrl = window.location.origin + '/auth/callback?role=contractor';
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="w-full h-13 py-3 mb-4 flex items-center justify-center gap-3 bg-white dark:bg-neutral-700 text-neutral-800 dark:text-white font-bold rounded-xl border-2 border-black dark:border-neutral-600 shadow-[3px_3px_0px_#000] active:translate-y-1 active:shadow-none transition-[transform,box-shadow]"
              data-testid="funnel-google-signup-btn"
            >
              <GoogleLogo size={22} weight="bold" className="text-[#4285F4]" />
              Skip the form — sign up with Google
            </button>
            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-neutral-200 dark:border-neutral-700" /></div>
              <div className="relative flex justify-center"><span className="px-3 text-[11px] font-bold uppercase text-neutral-400 bg-white dark:bg-neutral-800">or with email</span></div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Full Name *</label>
                  <input value={form.name} onChange={(e) => update('name', e.target.value)}
                    className={inputCls} placeholder="John Doe" data-testid="name-input" />
                </div>
                <div>
                  <label className={labelCls}>Phone</label>
                  <input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)}
                    className={inputCls} placeholder="(555) 123-4567" data-testid="phone-input" />
                </div>
              </div>
              <div>
                <label className={labelCls}>Email * <span className="text-neutral-400 normal-case font-normal">(this will be your username)</span></label>
                <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                  className={inputCls} placeholder="john@acmeconstruction.com" data-testid="email-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Password * <span className="text-neutral-400 normal-case font-normal">(min 6 chars)</span></label>
                  <input type="password" value={form.password} onChange={(e) => update('password', e.target.value)}
                    className={inputCls} placeholder="••••••••" data-testid="password-input" autoComplete="new-password" />
                </div>
                <div>
                  <label className={labelCls}>Confirm Password *</label>
                  <input type="password" value={form.confirm_password} onChange={(e) => update('confirm_password', e.target.value)}
                    className={`w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border rounded-xl focus:outline-none text-neutral-900 dark:text-white ${passwordMismatch ? 'border-red-500' : 'border-neutral-300 dark:border-neutral-600 focus:border-[#EA580C]'}`}
                    placeholder="••••••••" data-testid="confirm-password-input" autoComplete="new-password" />
                  {passwordMismatch && (
                    <p className="text-[11px] text-red-500 mt-1 font-bold" data-testid="password-mismatch">Passwords don't match</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Submit */}
          <div>
            <button onClick={handleSubmit} disabled={!validate() || submitting}
              className={`w-full flex items-center justify-center gap-2 h-14 font-bold text-base rounded-full active:scale-[0.98] transition-all ${validate() && !submitting ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm' : 'bg-neutral-200 dark:bg-neutral-700 text-neutral-400 dark:text-neutral-500 cursor-not-allowed'}`}
              data-testid="submit-btn">
              {submitting ? <Clock size={20} weight="bold" className="animate-spin" /> : <CheckCircle size={20} weight="bold" />}
              {submitting ? 'Setting up your account...' : 'Join as Contractor — Free'}
            </button>
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400 pt-3 text-center">By joining, you agree to ConstructHelp's Terms of Service and Privacy Policy. Already a member? <button type="button" onClick={() => navigate('/login')} className="text-[#EA580C] font-bold hover:underline" data-testid="goto-login-link">Log in</button></p>
          </div>
        </div>

        {/* Trust badges */}
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-white dark:bg-neutral-800 border-2 border-[#EA580C] rounded-lg p-3 text-center relative" data-testid="trust-badge-ai-scheduler">
            <span className="absolute -top-2 -right-2 bg-[#EA580C] text-white text-[8px] font-black uppercase px-1.5 py-0.5 border border-black rounded">NEW</span>
            <CalendarCheck size={20} weight="fill" className="text-[#EA580C] mx-auto mb-1" />
            <p className="text-[11px] font-bold text-neutral-700 dark:text-neutral-300">AI Crew Scheduling</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-[#EA580C] rounded-lg p-3 text-center relative" data-testid="trust-badge-ai-photo">
            <span className="absolute -top-2 -right-2 bg-[#EA580C] text-white text-[8px] font-black uppercase px-1.5 py-0.5 border border-black rounded">NEW</span>
            <Camera size={20} weight="fill" className="text-[#EA580C] mx-auto mb-1" />
            <p className="text-[11px] font-bold text-neutral-700 dark:text-neutral-300">AI Photo Estimator</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-emerald-500 rounded-lg p-3 text-center relative" data-testid="trust-badge-translator">
            <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[8px] font-black uppercase px-1.5 py-0.5 border border-black rounded">NEW</span>
            <Microphone size={20} weight="fill" className="text-emerald-500 mx-auto mb-1" />
            <p className="text-[11px] font-bold text-neutral-700 dark:text-neutral-300">Voice Translator</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-teal-500 rounded-lg p-3 text-center relative" data-testid="trust-badge-shopping-lists">
            <span className="absolute -top-2 -right-2 bg-teal-500 text-white text-[8px] font-black uppercase px-1.5 py-0.5 border border-black rounded">NEW</span>
            <ShoppingCart size={20} weight="fill" className="text-teal-500 mx-auto mb-1" />
            <p className="text-[11px] font-bold text-neutral-700 dark:text-neutral-300">AI Shopping Lists</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-neutral-300 dark:border-neutral-700 rounded-lg p-3 text-center">
            <Tag size={20} weight="bold" className="text-[#EA580C] mx-auto mb-1" />
            <p className="text-[11px] font-bold text-neutral-700 dark:text-neutral-300">Pay only for leads you want</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-neutral-300 dark:border-neutral-700 rounded-lg p-3 text-center">
            <ShieldCheck size={20} weight="bold" className="text-emerald-500 mx-auto mb-1" />
            <p className="text-[11px] font-bold text-neutral-700 dark:text-neutral-300">Escrow-backed payouts</p>
          </div>
        </div>

        {/* Feature promos (below the form) */}
        <div className="mt-6 relative overflow-hidden bg-gradient-to-br from-[#1A1A1A] to-[#2D2D2D] border-2 border-[#EA580C] rounded-xl shadow-[4px_4px_0px_#EA580C]" data-testid="photo-ai-promo">
          <div className="absolute top-0 right-0 bg-[#EA580C] text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 border-b-2 border-l-2 border-black">NEW</div>
          <div className="p-5 flex items-start gap-4">
            <div className="flex-shrink-0 w-14 h-14 bg-[#EA580C] rounded-lg flex items-center justify-center border-2 border-black shadow-[2px_2px_0px_#000]">
              <Camera size={28} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Sparkle size={14} weight="fill" className="text-[#EA580C]" />
                <span className="text-[10px] font-black uppercase tracking-widest text-[#EA580C]">GPT-5.2 Vision · Included Free</span>
              </div>
              <h3 className="font-black text-white text-lg leading-tight mb-1">Snap a photo. Get an instant scoped estimate.</h3>
              <p className="text-xs text-neutral-300 leading-relaxed">Upload up to 5 job-site photos and our AI inspects damage, scope, materials, and finishes — then returns a 3-tier (Economy / Standard / Premium) price range plus a line-item materials list. Win more bids in minutes.</p>
            </div>
          </div>
        </div>

        <div className="mt-6 relative overflow-hidden bg-gradient-to-br from-[#4a2b0f] to-[#2a1607] border-2 border-[#EA580C] rounded-xl shadow-[4px_4px_0px_#EA580C]" data-testid="daily-checklists-promo">
          <div className="absolute top-0 right-0 bg-[#EA580C] text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 border-b-2 border-l-2 border-black">NEW</div>
          <div className="p-5 flex items-start gap-4">
            <div className="flex-shrink-0 w-14 h-14 bg-[#EA580C] rounded-lg flex items-center justify-center border-2 border-black shadow-[2px_2px_0px_#000]">
              <ClipboardText size={28} weight="fill" className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <BellRinging size={14} weight="fill" className="text-[#EA580C]" />
                <span className="text-[10px] font-black uppercase tracking-widest text-[#EA580C]">Daily Checklists · Real-Time Proof</span>
              </div>
              <h3 className="font-black text-white text-lg leading-tight mb-1">Run your crew like a general.</h3>
              <p className="text-xs text-neutral-300 leading-relaxed">Push per-worker task lists to each job. Workers tap-to-check on their phone and attach photo proof + notes when it matters. You get pinged the second something closes. No group chats, no chasing status.</p>
              <div className="mt-3 flex items-start gap-2 bg-black/40 border border-[#EA580C] rounded-lg px-3 py-2" data-testid="voice-checklist-promo">
                <span className="flex-shrink-0 inline-flex items-center gap-1 bg-[#EA580C] text-black text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 border border-black mt-0.5">
                  <Microphone size={10} weight="fill" /> Voice · New
                </span>
                <p className="text-xs text-neutral-200 leading-relaxed">
                  <span className="font-black text-white">Dictate the whole checklist.</span> Speak freely — English or Spanish — and AI splits it into separate tasks. Workers dictate completion notes hands-free too.
                </p>
              </div>
              <VoiceTaskSplitMock className="mt-3" />
            </div>
          </div>
        </div>

        <div className="mt-6 relative overflow-hidden bg-gradient-to-br from-emerald-700 to-teal-900 border-2 border-emerald-400 rounded-xl shadow-[4px_4px_0px_#0d9488]" data-testid="voice-translator-promo">
          <div className="absolute top-0 right-0 bg-emerald-400 text-emerald-900 text-[10px] font-black uppercase tracking-widest px-3 py-1 border-b-2 border-l-2 border-black">NEW</div>
          <div className="p-5 flex items-start gap-4">
            <div className="flex-shrink-0 w-14 h-14 bg-emerald-400 rounded-lg flex items-center justify-center border-2 border-black shadow-[2px_2px_0px_#000]">
              <Microphone size={28} weight="fill" className="text-emerald-900" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Translate size={14} weight="fill" className="text-emerald-400" />
                <span className="text-[10px] font-black uppercase tracking-widest text-emerald-300">12 Languages · Included Free</span>
              </div>
              <h3 className="font-black text-white text-lg leading-tight mb-1">Talk to any crew. In any language.</h3>
              <p className="text-xs text-neutral-300 leading-relaxed">Tap the mic, speak any of 12 languages — English, Spanish, Portuguese, Polish, Mandarin & more — and our AI translates instantly, both ways. Construction jargon stays accurate (rebar → varilla, drywall → tablaroca, joist → viga). Built for the job site.</p>
            </div>
          </div>
        </div>

        {/* Promo video */}
        <div className="mt-6 bg-[#0F172A] border-2 border-black rounded-xl p-5 md:p-6" data-testid="funnel-promo-video">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 items-center">
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-[#EA580C] mb-2">▶ 60-second tour</p>
              <h3 className="text-xl font-black text-white uppercase tracking-tight mb-2">See what you're joining</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">Real leads, escrow-protected payouts, AI tools, and a crew-ready dashboard — watch how contractors run their whole business on ConstructHelp.</p>
            </div>
            <div className="flex justify-center sm:justify-end">
              <div className="w-full max-w-[220px] rounded-2xl border-2 border-white/20 bg-black overflow-hidden">
                <video
                  src="https://customer-assets-7cd3h4nn.emergentagent.net/job_58284b88-53d8-4c2d-a757-f70994c6780a/artifacts/vwt4er10_My%20movie%2053.mp4"
                  onPlay={() => trackVideoEvent('contractor-funnel-promo', 'play')}
                  onEnded={() => trackVideoEvent('contractor-funnel-promo', 'complete')}
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full aspect-[9/16] object-cover"
                  data-testid="funnel-promo-video-player"
                />
              </div>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-6 bg-neutral-100 dark:bg-neutral-800 border-2 border-neutral-300 dark:border-neutral-700 rounded-xl p-5">
          <h3 className="font-bold text-neutral-900 dark:text-white mb-3">Frequently Asked Questions</h3>
          {[
            { q: 'What is the AI Scheduling Engine?', a: 'Our newest tool. Describe any job in plain English and AI builds a phase-by-phase schedule with realistic durations and dependencies, then ranks your best available crew for each phase using skills, ratings, and availability calendars. If a phase slips, all downstream dates cascade automatically and your crew gets re-notified.' },
            { q: 'What is the AI Photo Estimator?', a: 'A brand-new tool included free with your account. Upload up to 5 job-site photos and GPT-5.2 Vision will inspect them, then return a 3-tier price range (Economy/Standard/Premium), a line-item materials list, and labor breakdown. Great for fast, defensible bids.' },
            { q: 'What is the Voice Translator?', a: 'A built-in voice translator supporting 12 languages (English, Spanish, Portuguese, French, Polish, Russian, Ukrainian, Mandarin, Vietnamese, Korean, Tagalog, Haitian Creole) powered by OpenAI Whisper, GPT-5.2, and Text-to-Speech. Tap the mic, speak, and hear the translation in seconds. Construction-specific terminology stays accurate (rebar ↔ varilla, drywall ↔ tablaroca, joist ↔ viga). Available on your dashboard the moment you sign up.' },
            { q: 'How much does it cost to join?', a: 'Joining is free. Contractors only pay for the leads they choose to purchase, plus optional Pro/Premium plans for higher visibility.' },
            { q: 'How do leads work?', a: 'Verified homeowner project requests appear in your marketplace. Pricing is set by lead grade (A-F) — premium leads cost more, basic leads are discounted.' },
            { q: 'What about my license & insurance?', a: 'You can add license + insurance now to earn a "Verified Pro" badge. Verified pros receive 2× more lead views on average.' },
            { q: 'Can I change my service area later?', a: 'Yes — you can adjust your trade, service area, and rates anytime from your dashboard.' },
          ].map((faq, i) => (
            <details key={i} className="mb-2 group">
              <summary className="cursor-pointer font-bold text-sm text-neutral-700 dark:text-neutral-300 flex items-center gap-2 py-1">
                <CaretDown size={14} weight="bold" className="text-[#EA580C] group-open:rotate-180 transition-transform" /> {faq.q}
              </summary>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 pl-6 pb-2">{faq.a}</p>
            </details>
          ))}
        </div>

        {/* Home link */}
        <div className="mt-6 pb-8 text-center">
          <a
            href="/home"
            data-testid="funnel-home-link"
            className="inline-flex items-center gap-2 text-sm font-bold text-[#2B4C6F] dark:text-neutral-300 hover:text-[#EA580C] transition-colors underline underline-offset-4"
          >
            ← Explore everything ConstructHelp offers
          </a>
        </div>
      </div>
    </div>
  );
}
